import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  PLATFORM_ID,
  viewChild,
  viewChildren,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import gsap from 'gsap';

@Component({
  selector: 'app-cosmic-hologram',
  standalone: true,
  templateUrl: './cosmic-hologram.html',
  styleUrl: './cosmic-hologram.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(mousemove)': 'onMouseMove($event)',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class CosmicHologram {
  private readonly hologramContainerRef =
    viewChild<ElementRef<HTMLDivElement>>('hologramContainer');

  private readonly centerGemRef =
    viewChild<ElementRef<HTMLDivElement>>('centerGem');

  private readonly satellites =
    viewChildren<ElementRef<HTMLDivElement>>('satellite');

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);
  private orbitAnim: gsap.core.Tween | null = null;

  constructor() {
    afterRenderEffect((onCleanup) => {
      if (!this.isBrowser) return;

      const satelliteList = this.satellites();
      if (satelliteList.length === 0) return;

      this.initAnimations();

      onCleanup(() => {
        if (this.orbitAnim) {
          this.orbitAnim.kill();
          this.orbitAnim = null;
        }

        const containerEl = this.hologramContainerRef()?.nativeElement;
        const centerEl = this.centerGemRef()?.nativeElement;
        const satelliteEls = satelliteList.map((s) => s.nativeElement);

        const elementsToKill = [centerEl, containerEl, ...satelliteEls].filter(
          Boolean,
        );

        if (elementsToKill.length > 0) {
          gsap.killTweensOf(elementsToKill);
        }
      });
    });

    this.destroyRef.onDestroy(() => {
      if (this.isBrowser && this.orbitAnim) {
        this.orbitAnim.kill();
        this.orbitAnim = null;
      }
    });
  }

  private initAnimations(): void {
    const centerEl = this.centerGemRef()?.nativeElement;
    if (centerEl) {
      gsap.to(centerEl, {
        y: -12,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.easeInOut',
      });
    }

    const satelliteEls = this.satellites().map((s) => s.nativeElement);

    const totalSatellites = satelliteEls.length;

    // Limpa CSS translate do Tailwind v4 (que conflita com GSAP transform)
    satelliteEls.forEach((el) => {
      el.style.translate = 'none';
    });

    // Objeto dummy para guiar a animação de 0 a 2*PI (360 graus)
    const orbitTracker = { angle: 0 };

    this.orbitAnim = gsap.to(orbitTracker, {
      angle: Math.PI * 2,
      duration: 25,
      ease: 'none',
      repeat: -1,
      onUpdate: () => {
        satelliteEls.forEach((el, i) => {
          // Espaça os satélites igualmente ao redor da órbita
          const offset = ((Math.PI * 2) / totalSatellites) * i;
          const currentAngle = orbitTracker.angle + offset;

          const { x, y } = this.getOrbitPosition(currentAngle);

          const isFront = Math.sin(currentAngle) > 0;
          const scale = 0.88 + Math.sin(currentAngle) * 0.14;
          const zIndex = isFront ? 30 : 10;
          const z = Math.sin(currentAngle) * 60;
          const opacity = 0.78 + Math.sin(currentAngle) * 0.22;

          gsap.set(el, {
            xPercent: -50,
            yPercent: -50,
            x: x,
            y: y,
            z: z,
            scale: scale,
            zIndex: zIndex,
            opacity: opacity,
          });
        });
      },
    });
  }

  private getOrbitPosition(angle: number): { x: number; y: number } {
    return {
      x: Math.cos(angle) * 230,
      y: Math.sin(angle) * 80,
    };
  }

  pauseOrbit(): void {
    this.orbitAnim?.pause();
  }

  resumeOrbit(): void {
    this.orbitAnim?.resume();
  }

  onMouseMove(event: MouseEvent): void {
    const containerEl = this.hologramContainerRef()?.nativeElement;
    if (!this.isBrowser || !containerEl) return;

    const rect = containerEl.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const normX = (event.clientX - centerX) / (rect.width / 2);
    const normY = (event.clientY - centerY) / (rect.height / 2);

    const tiltX = -normY * 15;
    const tiltY = normX * 15;

    // Efeito de parallax apenas no container (todo o holograma)
    gsap.to(containerEl, {
      rotateX: tiltX,
      rotateY: tiltY,
      duration: 0.4,
      ease: 'power2.out',
    });
  }

  onMouseLeave(): void {
    const containerEl = this.hologramContainerRef()?.nativeElement;
    if (!this.isBrowser || !containerEl) return;

    gsap.to(containerEl, {
      rotateX: 0,
      rotateY: 0,
      duration: 0.6,
      ease: 'power2.out',
    });
  }
}
